import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'motion/react';
import { useFinePointer } from '../../hooks/useMediaQuery';

const INTERACTIVE = 'a, button, [role="button"], summary, label, [data-cursor]';
const TEXT_ENTRY = 'input, textarea, select, [contenteditable="true"]';

/*
 * A soft ring that trails the (still visible) system cursor. It swells over interactive
 * elements and shows a word for elements with data-cursor="View" etc. Mouse/trackpad
 * only; off for reduced motion; hides over text fields so it never covers the caret.
 */
const Cursor = () => {
  const fine = useFinePointer();
  const reduced = useReducedMotion();
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 520, damping: 42, mass: 0.45 });
  const sy = useSpring(y, { stiffness: 520, damping: 42, mass: 0.45 });
  const [state, setState] = useState({ visible: false, hover: false, label: '', down: false });
  const last = useRef(state);

  useEffect(() => {
    if (!fine || reduced) return undefined;
    const update = patch => {
      const next = { ...last.current, ...patch };
      if (Object.keys(patch).some(k => next[k] !== last.current[k])) {
        last.current = next;
        setState(next);
      }
    };
    const onMove = e => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!last.current.visible) update({ visible: true });
    };
    const onOver = e => {
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(TEXT_ENTRY)) return update({ visible: false });
      const el = target?.closest(INTERACTIVE);
      update({ visible: true, hover: !!el, label: el?.getAttribute('data-cursor') || '' });
    };
    const onDown = () => update({ down: true });
    const onUp = () => update({ down: false });
    const onLeave = () => update({ visible: false });

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerover', onOver, { passive: true });
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('pointerup', onUp);
    document.documentElement.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', onUp);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, [fine, reduced, x, y]);

  if (!fine || reduced) return null;

  const size = state.label ? 84 : state.hover ? 52 : 26;

  return (
    <motion.div aria-hidden="true" className="pointer-events-none fixed left-0 top-0 z-[90]" style={{ x: sx, y: sy }}>
      <motion.div
        className={`flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-colors duration-300 ${
          state.label ? 'border-brand/70 bg-brand/90' : state.hover ? 'border-brand/70 bg-brand/[0.08]' : 'border-foreground/35 bg-transparent'
        }`}
        animate={{ width: size, height: size, opacity: state.visible ? 1 : 0, scale: state.down ? 0.86 : 1 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      >
        {state.label && <span className="font-mono text-[0.625rem] font-medium uppercase tracking-[0.14em] text-[hsl(var(--on-brand-fill))]">{state.label}</span>}
      </motion.div>
    </motion.div>
  );
};

export default Cursor;
